import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAlertView } from '~/app';
import { LoadingSpinner } from '~/components';
import { Button } from '~/components/buttons/Button/Button';
import { PlatformAgreementDialog } from './PlatformAgreementDialog';
import { returnToMedCredVault } from './returnToMedCredVault';
import { acceptHlxTerms, getHlxTermsStatus } from './terms.api';

type GateStatus = 'loading' | 'required' | 'accepted' | 'unavailable';

/**
 * [MCV-1815] A provider must accept the Platform Participation Agreement once
 * before using HLX. Wrap only the provider's HLX view: employers and admins are
 * not gated, and neither is an employer impersonating a provider (that session
 * is a different auth state, so the caller never mounts this gate for it).
 *
 * Fails closed: while the status loads or if it cannot be read, HLX is not
 * shown.
 */
export const HlxProviderTermsGate = ({ children }: { children: ReactNode }) => {
  const alertView = useAlertView();
  // The status read must run once per attempt, not whenever the alert handle changes identity.
  const alertRef = useRef(alertView);
  alertRef.current = alertView;
  const [status, setStatus] = useState<GateStatus>('loading');
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    getHlxTermsStatus(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setStatus(result.accepted ? 'accepted' : 'required');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Could not read the Platform Participation Agreement status', error);
        setStatus('unavailable');
        alertRef.current.send({
          type: 'ON_SHOW_ALERT',
          payload: {
            text: 'Could not check the Platform Participation Agreement',
            subText: 'Please try again.',
            type: 'error',
            timeout: 5000,
          },
        });
      });
    return () => controller.abort();
  }, [attempt]);

  const accept = useCallback(async () => {
    setSaving(true);
    try {
      const result = await acceptHlxTerms();
      setStatus(result.accepted ? 'accepted' : 'required');
    } catch (error) {
      console.error('Could not record the Platform Participation Agreement', error);
      // The dialog stays open so the provider can try again.
      alertRef.current.send({
        type: 'ON_SHOW_ALERT',
        payload: {
          text: 'Could not save your acceptance',
          subText: 'Please try again.',
          type: 'error',
          timeout: 5000,
        },
      });
    } finally {
      setSaving(false);
    }
  }, []);

  if (status === 'accepted') return <>{children}</>;

  if (status === 'unavailable') {
    return (
      <div
        className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center"
        data-cy="hlx-terms-unavailable"
      >
        <p>We could not check the Platform Participation Agreement.</p>
        <div className="flex gap-3">
          <Button type="button" colorVariant="tertiary" onClick={returnToMedCredVault}>
            Return to MedCredVault
          </Button>
          <Button type="button" onClick={() => setAttempt((count) => count + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <LoadingSpinner />
      <PlatformAgreementDialog
        open={status === 'required'}
        dismissible={false}
        description="Before you use Healthcare Labor Exchange, please review and accept the Platform Participation Agreement. You only need to do this once."
        primaryLabel="Accept and continue"
        secondaryLabel="Return to MedCredVault"
        busy={saving}
        onAccept={accept}
        onSecondary={returnToMedCredVault}
      />
    </>
  );
};
